import createContainer from 'constate';
import React, { useContext, useEffect, useState } from 'react';
import { nameHOC } from './hocutils';
import { usePriceMapper } from './hooks/usePriceMapper';
import _ from './LodashImports';
import { groupByBackendCategoryId } from './RecoHelper';

function useBuyTogetherContext() {
  const [data, setData] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isNoItemSelected, setIsNoItemSelected] = useState(false);
  const { mapPrices } = usePriceMapper();
  const [widgetName, setWidgetName] = useState('');

  useEffect(() => {
    if (data.length === 0) return;
    setIsAllSelected(data.every((variant) => variant.isSelected));
    setIsNoItemSelected(data.every((variant) => !variant.isSelected));
  }, [data]);

  const getCanonicalUrl = ({ variantId }) => {
    const item = data.find((product) => product.id === variantId);
    return item ? item.url : null;
  };

  const resetData = () => {
    setData((prev) =>
      prev.map((item) => ({
        ...item,
        isSelected: false,
        quantity: 1,
      }))
    );
  };

  function mapData(apiResponse, limit = 6) {
    const recoProducts = groupByBackendCategoryId({
      products: _.get(apiResponse, '[0].data', []),
      filterOptions: {
        maxGroups: limit,
        podsByGroup: 1,
        minPods: 2,
      },
    });
    if (!recoProducts.length) {
      return;
    }
    const name = _.get(apiResponse, '[0].name', '');
    setWidgetName(name);

    const mappedData = recoProducts.map((product) => ({
      id: product.id,
      parentId: product.productId,
      name: product.displayName,
      brandName: product.brand,
      image: product.mediaUrls[0] || '',
      quantity: 1,
      price: mapPrices(product),
      url: product.url,
      isSelected: false,
      offeringId: product.offeringId,
      recommendedUnits: product.recommendedUnits || '',
    }));

    setData(mappedData);
  }

  const onVariantSelection = (variantId) => {
    const id = Array.isArray(variantId) ? variantId[0].trim() : variantId;
    setData((prev) => prev.map((v) => (v.id === id ? { ...v, isSelected: !v.isSelected } : v)));
  };

  const handleSelectAll = () => {
    setData((prev) => prev.map((v) => ({ ...v, isSelected: !isAllSelected })));
  };

  const handleQtyChange = (variantId, qty) => {
    setData((prev) => prev.map((v) => (v.id === variantId ? { ...v, quantity: qty, isSelected: qty > 0 } : v)));
  };

  return {
    data,
    setData,
    widgetName,
    setWidgetName,
    isAllSelected,
    setIsAllSelected,
    isNoItemSelected,
    setIsNoItemSelected,
    onVariantSelection,
    handleSelectAll,
    mapData,
    handleQtyChange,
    getCanonicalUrl,
    resetData,
  };
}

const BuyTogetherContext = createContainer(useBuyTogetherContext);

const withBuyTogether = (WrappedComponent) => {
  const { getInitialProps } = WrappedComponent;

  const ComponentWithBuyTogetherData = (props) => {
    const buyTogetherDataWithState = useContext(BuyTogetherContext.Context);
    return <WrappedComponent {...props} buytogether={buyTogetherDataWithState} />;
  };

  if (typeof getInitialProps === 'function') {
    ComponentWithBuyTogetherData.getInitialProps = getInitialProps;
  }

  ComponentWithBuyTogetherData.originalName = nameHOC(WrappedComponent);
  ComponentWithBuyTogetherData.displayName = nameHOC(WrappedComponent, 'WithBuyTogether');

  return ComponentWithBuyTogetherData;
};

export default BuyTogetherContext;
export { withBuyTogether };
